/**
 * What the roster screen says about positions (slice 11e): pure, no DOM. Every grade here is the
 * sim's own `slotGrade` and every stat the slot's `gradeStat`; nothing is re-derived. A pick card's
 * badges are the positions it lists (`listedPositions`, balance.json `positionFit.listing`) with
 * its best grade at each; the key is built from the slots' stats and `roster.grades.thresholds`
 * (slice 10i: one triple a board-line side, through the sim's `slotThresholds`), so a balance
 * change updates the words (PRD S7's rule for numbers on screen); what the chart still needs is an
 * exact matching of the depth chart's slots to the picks that list them. Slice 11f: the badges are
 * pills, and what is still needed is a count on each position chip (`positionChips`). Slice 11g:
 * four pills, the words for every battle a slot fights (its board line's, then the ones its roles
 * fight across the playbook), and the Lineup row's sub-line (`slotLine`), which replace 11e's
 * grade tap.
 */
import { positionsOf } from '@tdmons/data/roster-draft';
import {
  DEFAULT_PLAYBOOK,
  DEFENSE_SLOTS,
  OFFENSE_SLOTS,
  SLOT_POSITION,
  balance,
  combatantOf,
  gradeStat,
  lineOf,
  lostBattlesBeforeTired,
  referenceStamina,
  sideStat,
  slotGrade,
  slotThresholds,
  statsAtLevel,
  statsOf,
  type Balance,
  type BoardKind,
  type Combatant,
  type Creature,
  type DefenseRole,
  type Grade,
  type GradeStat,
  type MatchupKind,
  type OffenseRole,
  type Playbook,
  type Position,
  type RosterSlot,
} from '@tdmons/sim';

/**
 * A card shows its best this-many positions as pills: two rows of two on a 390 px card (slice 11f
 * measured two to a row in the pixel font). Slice 11g: a Pokémon that lists more says how many
 * under them ("9 positions · tap for all ›") in place of 11f's `+N`.
 */
export const CARD_BADGES = 4;

const SLOTS: readonly RosterSlot[] = [...OFFENSE_SLOTS, ...DEFENSE_SLOTS];

/** The positions in depth-chart order: QB RB WR TE OL, then DL LB CB S. */
export const POSITION_ORDER: readonly Position[] = [...new Set(SLOTS.map((s) => SLOT_POSITION[s]))];

type OneStat = ReturnType<typeof statsOf>[number];

/** One stat read alone, in words. */
const ONE_STAT_WORDS: Record<OneStat, string> = {
  atk: 'Attack',
  def: 'Defense',
  spa: 'Sp. Atk',
  spd: 'Sp. Def',
  spe: 'Speed',
};
/** One stat of an averaged pair, in words. */
export const SHORT_STAT_WORDS: Record<OneStat, string> = {
  atk: 'Atk',
  def: 'Def',
  spa: 'Sp. Atk',
  spd: 'Sp. Def',
  spe: 'Speed',
};

/**
 * A battle side's stat in words, from balance.json `battles` through the sim's key: "Speed", and
 * an averaged pair as "Atk + Def" (slice 10g: the words follow the map).
 */
export function statWords(stat: GradeStat): string {
  const stats = statsOf(stat);
  return stats.length === 1
    ? ONE_STAT_WORDS[stats[0]]
    : stats.map((s) => SHORT_STAT_WORDS[s]).join(' + ');
}

/** What a slot does with its stat, by board line and side: "QB throws with Sp. Atk". */
const VERBS: Record<BoardKind, { offense: string; defense: string }> = {
  block: { offense: 'block', defense: 'hit' },
  tackle: { offense: 'break tackles', defense: 'hit' },
  coverage: { offense: 'run', defense: 'run' },
  throw: { offense: 'throw', defense: 'run' },
};

export const GRADE_RANK: Record<Grade, number> = { A: 3, B: 2, C: 1, D: 0 };

function verbOf(slot: RosterSlot): string {
  const line = lineOf(slot);
  return VERBS[line.kind][line.offense === slot ? 'offense' : 'defense'];
}

/** "break tackles" → "breaks tackles": the verb for one subject. */
const singular = (verb: string) => verb.replace(/^(\S+)/, '$1s');

/** "A", "A and B", "A, B and C". */
function list(words: readonly string[]): string {
  if (words.length < 2) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words.at(-1)}`;
}

export interface PositionGrade {
  position: Position;
  grade: Grade;
  /** The level-`pvp.level` stat the grade is from. */
  value: number;
}

/**
 * A creature's listed positions in depth-chart order, each with its best `slotGrade` over that
 * position's slots: fresh at level `pvp.level`, full HP, fit 1 (it lists the position).
 */
export function positionGrades(creature: Creature, b: Balance = balance): PositionGrade[] {
  const listed = positionsOf(creature, b);
  const c = combatantOf(creature, [], listed, b);
  return POSITION_ORDER.filter((p) => listed.includes(p)).map((position) => {
    let best: PositionGrade | undefined;
    for (const slot of SLOTS) {
      if (SLOT_POSITION[slot] !== position) continue;
      const g = slotGrade(c, slot, b);
      if (!best || g.value > best.value) best = { position, grade: g.grade, value: g.value };
    }
    return best as PositionGrade;
  });
}

const byGrade = (a: PositionGrade, b: PositionGrade) =>
  GRADE_RANK[b.grade] - GRADE_RANK[a.grade] ||
  POSITION_ORDER.indexOf(a.position) - POSITION_ORDER.indexOf(b.position);

/** A card's badges: its best `max` positions, by grade (A first) then depth chart, and the rest. */
export function cardBadges(
  grades: readonly PositionGrade[],
  max = CARD_BADGES,
): { shown: PositionGrade[]; more: number } {
  const sorted = [...grades].sort(byGrade);
  return { shown: sorted.slice(0, max), more: Math.max(0, sorted.length - max) };
}

/** "QB A · TE B · LB B", and with more than shown "QB A · RB A · WR A · TE A · 9 positions". */
export function badgeText(badges: { shown: readonly PositionGrade[]; more: number }): string {
  const text = badges.shown.map((g) => `${g.position} ${g.grade}`).join(' · ');
  return badges.more > 0 ? `${text} · ${badges.shown.length + badges.more} positions` : text;
}

/** The level-`pvp.level` stat of a base stat (every non-HP stat uses the same formula). */
export function levelStat(base: number, b: Balance = balance): number {
  return statsAtLevel(
    { hp: base, atk: base, def: base, spa: base, spd: base, spe: base },
    b.pvp.level,
  ).atk;
}

/**
 * The slots grouped by a key, each group naming who is in it: whole positions first (every slot
 * of the position shares the key), then the slots of a position whose slots differ (LB), in
 * depth-chart order. Insertion order is the slots'.
 */
function groupSlots<K>(
  keyOf: (slot: RosterSlot) => K,
): Map<K, { who: string[]; slots: RosterSlot[] }> {
  const groups = new Map<K, { whole: string[]; split: string[]; slots: RosterSlot[] }>();
  for (const slot of SLOTS) {
    const key = keyOf(slot);
    const position = SLOT_POSITION[slot];
    const g = groups.get(key) ?? { whole: [], split: [], slots: [] };
    groups.set(key, g);
    g.slots.push(slot);
    const same = SLOTS.filter((s) => SLOT_POSITION[s] === position).every((s) => keyOf(s) === key);
    if (!same) g.split.push(slot);
    else if (!g.whole.includes(position)) g.whole.push(position);
  }
  return new Map(
    [...groups].map(([k, g]) => [k, { who: [...g.whole, ...g.split], slots: g.slots }]),
  );
}

/** A threshold as the sheet prints it: as stored, a whole number without its `.0`. */
const bar = (t: number) => String(t);

/**
 * A share of battles in words (slice 10i): the smallest denominator up to 10 that says it exactly,
 * "9 in 10", "3 in 4", "half"; otherwise a percentage. `battles` puts the noun in: "9 battles in 10".
 */
export function shareWords(share: number, battles = false): string {
  for (let d = 2; d <= 10; d += 1) {
    const n = share * d;
    if (Math.abs(n - Math.round(n)) < 1e-9) {
      if (d === 2 && Math.round(n) === 1) return 'half';
      return `${Math.round(n)}${battles ? ' battles' : ''} in ${d}`;
    }
  }
  return `${Math.round(share * 100)}%${battles ? ' of battles' : ''}`;
}

/** "at or above half" in the owner's words: "more than half" for C at 0.5, "n in d or more" else. */
const atLeast = (share: number) =>
  share === 0.5 ? 'more than half' : `${shareWords(share)} or more`;

/** Who stands across under `roster.grades.field`, in words. */
function fieldWords(b: Balance): string {
  return b.roster.grades.field === 'listers'
    ? 'every Pokémon that lists the position across from him'
    : 'every Pokémon in the Dex that could stand across from him';
}

/**
 * What the letters mean (slice 10i, the owner's words): "A wins 9 battles in 10 at this spot, B
 * 3 in 4, C more than half, D less than half: fresh, at level 50, types and moves aside, against
 * every Pokémon in the Dex that could stand across from him." Every number is `roster.grades`.
 */
export function sharesSentence(b: Balance = balance): string {
  const { A, B, C } = b.roster.grades.shares;
  return (
    `A wins ${shareWords(A, true)} at this spot, B ${shareWords(B)}, C ${atLeast(C)}, D less than ` +
    `${shareWords(C)}: fresh, at level ${b.pvp.level}, types and moves aside, against ${fieldWords(b)}.`
  );
}

/** "half HP" at a fatigue threshold of 0.5; "40% HP" otherwise. */
export function tiredWords(b: Balance = balance): string {
  const t = b.hp.fatigueThreshold;
  return t === 0.5 ? 'half HP' : `${Math.round(t * 100)}% HP`;
}

/**
 * What stamina counts (slice 10i): "Lasts N counts the lost battles before he tires, under half
 * HP, in a Pro game; most last about 6." The count is the sim's `lostBattlesBeforeTired`; "most"
 * is the reference Pokémon's (`referenceStamina`).
 */
export function staminaSentence(b: Balance = balance): string {
  return (
    `Lasts N counts the lost battles before he tires, under ${tiredWords(b)}, in a Pro game; ` +
    `most last about ${referenceStamina(b)}.`
  );
}

/**
 * The key: which stat each slot fights with, grouped by stat, then one sentence for what the
 * letters win and one for what stamina counts (slice 10i). A position whose slots read different
 * stats (LB) is named by slot, after the whole positions. The per-slot bars ("A here is 146+") are
 * the Lineup sub-lines' and the sheet's, not the key's. `statOf` is the sim's `gradeStat`; a test
 * may hand another.
 */
export function gradeKey(
  b: Balance = balance,
  statOf: (slot: RosterSlot) => GradeStat = (slot) => gradeStat(slot, b),
): string {
  const parts = [...groupSlots(statOf)].map(([stat, g]) => {
    const verbs: string[] = [];
    for (const slot of g.slots) if (!verbs.includes(verbOf(slot))) verbs.push(verbOf(slot));
    const v = list(verbs);
    return `${list(g.who)} ${g.who.length === 1 ? singular(v) : v} with ${statWords(stat)}`;
  });
  return `${parts.join(' · ')}. ${sharesSentence(b)} ${staminaSentence(b)}`;
}

export interface Need {
  position: Position;
  count: number;
}

/**
 * The slots no pick lists, by position in depth-chart order. Each side is an exact matching of
 * its slots to the picks that list the slot's position, each pick in at most one slot a side (so
 * a both-ways pick covers one offense and one defense slot); among the best matchings the earlier
 * slot is covered first, so the chips count the later ones.
 */
export function stillNeed(listed: readonly (readonly Position[])[]): Need[] {
  const open: Position[] = [];
  for (const slots of [OFFENSE_SLOTS, DEFENSE_SLOTS] as readonly (readonly RosterSlot[])[]) {
    const n = listed.length;
    const k = slots.length;
    const size = 1 << n;
    // best[j][mask]: the most slots j.. covered with the picks in `mask` already used.
    const best = Array.from({ length: k + 1 }, () => new Int8Array(size));
    const pick = Array.from({ length: k }, () => new Int8Array(size).fill(-1));
    for (let j = k - 1; j >= 0; j -= 1) {
      const position = SLOT_POSITION[slots[j] as RosterSlot];
      for (let mask = 0; mask < size; mask += 1) {
        let top = (best[j + 1] as Int8Array)[mask] as number;
        let choice = -1;
        for (let i = 0; i < n; i += 1) {
          if (mask & (1 << i) || !listed[i]?.includes(position)) continue;
          const v = 1 + ((best[j + 1] as Int8Array)[mask | (1 << i)] as number);
          if (v > top || (v === top && choice < 0)) {
            top = v;
            choice = i;
          }
        }
        (best[j] as Int8Array)[mask] = top;
        (pick[j] as Int8Array)[mask] = choice;
      }
    }
    let mask = 0;
    for (let j = 0; j < k; j += 1) {
      const i = (pick[j] as Int8Array)[mask] as number;
      if (i < 0) open.push(SLOT_POSITION[slots[j] as RosterSlot]);
      else mask |= 1 << i;
    }
  }
  return POSITION_ORDER.flatMap((position) => {
    const count = open.filter((p) => p === position).length;
    return count > 0 ? [{ position, count }] : [];
  });
}

export interface PositionChip {
  position: Position;
  /** The slots `stillNeed` leaves open at this position; 0 is covered. */
  need: number;
}

/** The Team tab's position chips (slice 11f): all nine in depth-chart order, each with its count. */
export function positionChips(listed: readonly (readonly Position[])[]): PositionChip[] {
  const need = stillNeed(listed);
  return POSITION_ORDER.map((position) => ({
    position,
    need: need.find((n) => n.position === position)?.count ?? 0,
  }));
}

/**
 * The cards a position chip shows: those listing `position`, best grade there first, then in the
 * order given (Dex order).
 */
export function byPosition<T extends { grades: readonly PositionGrade[] }>(
  items: readonly T[],
  position: Position,
): T[] {
  const at = (t: T) => t.grades.find((g) => g.position === position);
  return items
    .map((t, i) => ({ t, i, g: at(t) }))
    .filter((x) => x.g)
    .sort(
      (a, b) =>
        GRADE_RANK[(b.g as PositionGrade).grade] - GRADE_RANK[(a.g as PositionGrade).grade] ||
        a.i - b.i,
    )
    .map((x) => x.t);
}

// Slice 11g: every battle a slot fights, in words, and the Lineup row's sub-line -----------------

/** The battles a slot can be in: every matchup kind but the kick (the kicker is not a slot). */
export type BattleKind = Exclude<MatchupKind, 'kick'>;
export type Side = 'offense' | 'defense';
/** What a side does in each battle, for the sheet: "throws · Sp. Atk", "rushes · Attack". */
export const KIND_WORDS: Record<BattleKind, Record<Side, string>> = {
  block: { offense: 'blocks', defense: 'rushes' },
  coverage: { offense: 'gets open', defense: 'covers' },
  tackle: { offense: 'breaks tackles', defense: 'tackles' },
  contested: { offense: 'contested catch', defense: 'contested catch' },
  throw: { offense: 'throws', defense: 'closes on the throw' },
};

/**
 * The stat each side of a battle reads (a: offense, b: defense): balance.json `battles` through the
 * sim's `sideStat`, never restated here (slice 10g); a test holds the two together through the
 * sim's own `resolveMatchup`.
 */
export function kindStat(kind: BattleKind, side: Side, b: Balance = balance): GradeStat {
  return sideStat(kind, side, b);
}

/**
 * The battles each play role fights (play.ts, run.ts): a route gets open, is caught contested and
 * breaks tackles after the catch; a blocker blocks; a carrier breaks tackles; a pass is the throw;
 * a rusher or blitzer is blocked and tackles; zone and man cover, contest the catch and tackle.
 */
export const ROLE_KINDS: Record<OffenseRole | DefenseRole, readonly BattleKind[]> = {
  pass: ['throw'],
  'hand-off': [],
  block: ['block'],
  route: ['coverage', 'contested', 'tackle'],
  carry: ['tackle'],
  rush: ['block', 'tackle'],
  blitz: ['block', 'tackle'],
  zone: ['coverage', 'contested', 'tackle'],
  man: ['coverage', 'contested', 'tackle'],
};

const KIND_ORDER: readonly BattleKind[] = ['throw', 'block', 'coverage', 'contested', 'tackle'];

export const sideOf = (slot: RosterSlot): Side =>
  (OFFENSE_SLOTS as readonly string[]).includes(slot) ? 'offense' : 'defense';

export interface Battle {
  kind: BattleKind;
  /** "contested catch" */
  words: string;
  /** "Sp. Def" */
  stat: string;
}

/** A side's battle of `kind` in words. */
export function battle(kind: BattleKind, side: Side, b: Balance = balance): Battle {
  return { kind, words: KIND_WORDS[kind][side], stat: statWords(kindStat(kind, side, b)) };
}

/**
 * The other battles a position can be in, beyond `slot`'s board line: each of its slots' board
 * line and the battles its roles fight across the playbook (so the list grows with the plays).
 */
export function secondaryBattles(
  slot: RosterSlot,
  playbook: Playbook = DEFAULT_PLAYBOOK,
  b: Balance = balance,
): Battle[] {
  const side = sideOf(slot);
  const position = SLOT_POSITION[slot];
  const kinds = new Set<BattleKind>();
  for (const s of SLOTS) {
    if (SLOT_POSITION[s] !== position) continue;
    kinds.add(lineOf(s).kind);
    const plays = side === 'offense' ? playbook.offense : playbook.defense;
    for (const p of plays) {
      const role = (p.assignments as Record<string, { role: OffenseRole | DefenseRole }>)[s]?.role;
      for (const k of role ? ROLE_KINDS[role] : []) kinds.add(k);
    }
  }
  kinds.delete(lineOf(slot).kind);
  return KIND_ORDER.filter((k) => kinds.has(k)).map((k) => battle(k, side, b));
}

/**
 * "A here is 146+", "D here is under 95.1": a grade's bar at `slot`, from its board-line side's
 * triple (slice 10i: `slotThresholds`), in level-`pvp.level` stat units as the sheet prints them.
 */
export function gradeBar(grade: Grade, slot: RosterSlot, b: Balance = balance): string {
  const t = slotThresholds(slot, b);
  return grade === 'D' ? `D here is under ${bar(t.C)}` : `${grade} here is ${bar(t[grade])}+`;
}

/** The stats a slot's grade stat is made of at level `pvp.level`: "Atk 154 + Def 115", "Speed 140". */
export function rawStat(c: Combatant, stat: GradeStat): string {
  const stats = statsOf(stat);
  return stats.length === 1
    ? `${statWords(stat)} ${c.stats[stats[0]]}`
    : stats.map((s) => `${SHORT_STAT_WORDS[s]} ${c.stats[s]}`).join(' + ');
}

export interface LineSegment {
  text: string;
  /** The out-of-position penalty, shown in the super-effective red. */
  out?: true;
}

/**
 * A Lineup row's sub-line (slice 11g): the stat at level `pvp.level`, the position-fit penalty
 * when there is one, the arrow to what the grade reads, and the bar it clears. Out of position:
 * "Speed 140 · x0.9 not listed at TE → 126 · A here is 146+ · lasts 5". Listed: "Atk 154 + Def 115
 * → 134 · listed OL · also DL2 · lasts 7" (the arrow only when the grade averages two stats). `also`
 * is the slot it holds on the other side; `lasts` is the stamina count (slice 10i). Every number is
 * `slotGrade`'s, `lostBattlesBeforeTired`'s or the combatant's own.
 */
export function slotLine(
  c: Combatant,
  slot: RosterSlot,
  also?: RosterSlot,
  b: Balance = balance,
): LineSegment[] {
  const g = slotGrade(c, slot, b);
  const position = SLOT_POSITION[slot];
  const value = Math.floor(g.value);
  const raw = rawStat(c, g.stat);
  const out: LineSegment[] =
    g.fit < 1
      ? [
          { text: `${raw} · ` },
          { text: `x${g.fit} not listed at ${position}`, out: true },
          { text: ` → ${value} · ${gradeBar(g.grade, slot, b)}` },
        ]
      : [
          {
            text: `${raw}${statsOf(g.stat).length === 2 ? ` → ${value}` : ''} · listed ${position}`,
          },
        ];
  if (also) out.push({ text: ` · also ${also}` });
  // Slice 10i: the stamina count, the second mark, at the end of every row.
  out.push({ text: ` · lasts ${lostBattlesBeforeTired(c.stats.hp, b)}` });
  return out;
}
